import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart, Truck, RotateCcw, Boxes, Building2, Store, Users, Headset,
  ReceiptText, History, Settings, Warehouse, LayoutDashboard, Search, PlusCircle,
  Moon, Sun, LogOut,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { NAV_ITEMS } from "@/lib/constants/navigation";
import { canAccessModule, canWrite } from "@/lib/permissions/permissions";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";

const QUICK_ACTIONS = [
  { key: "new-order", label: "New Order", icon: ShoppingCart, module: "orders", to: "/orders?new=1" },
  { key: "new-inbound", label: "New Inbound", icon: Truck, module: "inbound", to: "/inbound?new=1" },
  { key: "new-return", label: "New Return", icon: RotateCcw, module: "returns", to: "/returns?new=1" },
  { key: "new-client", label: "New Client", icon: Building2, module: "clients", to: "/clients?new=1" },
  { key: "new-store", label: "New Store", icon: Store, module: "stores", to: "/stores?new=1" },
  { key: "new-warehouse", label: "New Warehouse", icon: Warehouse, module: "warehouses", to: "/warehouses?new=1" },
  { key: "invite-user", label: "Invite User", icon: Users, module: "users", to: "/users?new=1" },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const role = user?.role;

  useEffect(() => {
    const down = (e) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const navItems = useMemo(
    () => NAV_ITEMS.filter((i) => canAccessModule(role, i.key)),
    [role]
  );
  const quickActions = useMemo(
    () => QUICK_ACTIONS.filter((a) => canWrite(role, a.module)),
    [role]
  );

  const go = (to) => {
    setOpen(false);
    setTimeout(() => navigate(to), 40);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen} data-testid="command-palette">
      <CommandInput placeholder="Search or jump to…" data-testid="command-palette-input" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {quickActions.length > 0 && (
          <CommandGroup heading="Quick Create">
            {quickActions.map(({ key, label, icon: Icon, to }) => (
              <CommandItem key={key} onSelect={() => go(to)} data-testid={`cmd-${key}`}>
                <PlusCircle className="mr-2 h-4 w-4 text-primary" />
                <span>{label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandSeparator />

        <CommandGroup heading="Navigate">
          {navItems.map(({ key, label, to, icon: Icon }) => (
            <CommandItem key={key} onSelect={() => go(to)} data-testid={`cmd-nav-${key}`}>
              <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
              <span>{label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Preferences">
          <CommandItem onSelect={() => { toggleTheme(); }} data-testid="cmd-theme">
            {theme === "dark" ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
            <span>Switch to {theme === "dark" ? "light" : "dark"} mode</span>
          </CommandItem>
          <CommandItem
            onSelect={async () => { setOpen(false); await logout(); navigate("/login"); }}
            data-testid="cmd-logout"
          >
            <LogOut className="mr-2 h-4 w-4 text-rose-500" />
            <span>Sign out</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

// Small pill button that opens the palette — used in Topbar.
export function CommandPaletteTrigger({ className }) {
  return (
    <button
      type="button"
      data-testid="command-palette-trigger"
      onClick={() => {
        const ev = new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true });
        document.dispatchEvent(ev);
      }}
      className={`hidden items-center gap-2 rounded-lg border border-border/60 bg-background/60 px-3 py-1.5 text-xs text-muted-foreground transition-all hover:border-primary/40 hover:text-foreground focus-ring md:inline-flex ${className || ""}`}
      aria-label="Open search"
    >
      <Search className="h-3.5 w-3.5" />
      <span>Search…</span>
    </button>
  );
}
